import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { StepList } from '../components/StepList';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { EntrepriseForm } from '../components/EntrepriseForm';
import { Users, FileText, Target, CheckCircle, FileSignature, HeartHandshake, Search, Briefcase, Euro, Award, ArrowDownCircle } from 'lucide-react';
const ENTREPRISE_STEPS = [{
  title: 'Sourcing et définition du besoin',
  description: 'Analyse de votre poste et définition du profil idéal avec nos conseillers.',
  icon: <Search className="w-6 h-6" data-id="element-1088" />
}, {
  title: 'Présélection des candidats',
  description: 'Nous vous présentons uniquement des profils qualifiés, motivés et préparés.',
  icon: <CheckCircle className="w-6 h-6" data-id="element-1089" />
}, {
  title: 'Aide aux démarches administratives',
  description: "Accompagnement dans la rédaction et la signature du contrat d'apprentissage (CERFA).",
  icon: <FileSignature className="w-6 h-6" data-id="element-1090" />
}, {
  title: 'Suivi personnalisé',
  description: "Points réguliers avec le tuteur en entreprise tout au long du contrat pour garantir le succès de l'alternance.",
  icon: <HeartHandshake className="w-6 h-6" data-id="element-1091" />
}];
export function Entreprises() {
  const scrollToForm = () => {
    document.getElementById('formulaire-recrutement')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="flex flex-col w-full" data-id="element-1092">
      <PageHeader title="Espace Entreprises" subtitle="Recrutez vos futurs collaborateurs et dynamisez vos équipes grâce à l'alternance avec Zillenium." data-id="element-1093" />

      {/* Top Jump Button */}
      <div className="bg-primary-dark text-white pb-12 pt-4 text-center -mt-1" data-id="element-1094">
        <Button onClick={scrollToForm} className="bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-transform" data-id="element-1095">
          Confier un recrutement <ArrowDownCircle className="ml-2 w-5 h-5" data-id="element-1096" />
        </Button>
      </div>

      {/* Pourquoi recruter */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-1097">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-lavande rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1098" />
        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-1099">
          <SectionHeading title="Pourquoi recruter avec Zillenium Alternance ?" centered data-id="element-1100" />
          <div className="grid md:grid-cols-3 gap-8 mt-16" data-id="element-1101">
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-1102">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1103" />
              <CardContent className="p-8 text-center" data-id="element-1104">
                <div className="w-20 h-20 bg-lavande rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1105">
                  <Target className="w-10 h-10 text-brand-orange animate-soft-bounce" data-id="element-1106" />
                </div>
                <h3 className="font-extrabold font-heading text-2xl text-primary-dark mb-4" data-id="element-1107">
                  Des profils ciblés
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1108">
                  Des candidats préparés aux réalités du terrain et formés aux
                  techniques commerciales actuelles.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-1109">
              <div className="h-2 w-full bg-primary" data-id="element-1110" />
              <CardContent className="p-8 text-center" data-id="element-1111">
                <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1112">
                  <FileText className="w-10 h-10 text-primary animate-soft-float" data-id="element-1113" />
                </div>
                <h3 className="font-extrabold font-heading text-2xl text-primary-dark mb-4" data-id="element-1114">
                  Zéro tracas administratif
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1115">
                  Nous vous guidons dans toutes les étapes de contractualisation
                  de votre alternant.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-1116">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1117" />
              <CardContent className="p-8 text-center" data-id="element-1118">
                <div className="w-20 h-20 bg-lavande rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1119">
                  <Users className="w-10 h-10 text-brand-orange animate-soft-pulse" data-id="element-1120" />
                </div>
                <h3 className="font-extrabold font-heading text-2xl text-primary-dark mb-4" data-id="element-1121">
                  Un partenaire de proximité
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1122">
                  Un interlocuteur unique et réactif pour assurer le suivi de
                  votre apprenti en entreprise.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Missions en entreprise */}
      <section className="py-24 bg-muted" data-id="element-1123">
        <div className="container max-w-6xl mx-auto" data-id="element-1124">
          <SectionHeading title="Les missions en entreprise" subtitle="Quelles responsabilités confier à nos alternants pour les faire monter en compétences ?" centered data-id="element-1125" />
          <div className="grid md:grid-cols-2 gap-8 mt-16" data-id="element-1126">
            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow" data-id="element-1127">
              <CardContent className="p-8" data-id="element-1128">
                <div className="flex items-center gap-4 mb-6" data-id="element-1129">
                  <div className="w-12 h-12 bg-lavande rounded-xl flex items-center justify-center" data-id="element-1130">
                    <Briefcase className="w-6 h-6 text-primary-dark" data-id="element-1131" />
                  </div>
                  <h3 className="font-extrabold font-heading text-2xl text-primary-dark" data-id="element-1132">
                    Missions BTS NDRC
                  </h3>
                </div>
                <ul className="space-y-4 text-slate-600" data-id="element-1133">
                  <li className="flex items-start gap-3" data-id="element-1134">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1135" />
                    <span data-id="element-1136">
                      <strong data-id="element-1137">Prospection et développement :</strong> Recherche
                      de nouveaux clients, qualification de fichiers, prise de
                      rendez-vous.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1138">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1139" />
                    <span data-id="element-1140">
                      <strong data-id="element-1141">Négociation et vente :</strong> Conduite
                      d'entretiens commerciaux, élaboration de propositions,
                      conclusion de ventes.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1142">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1143" />
                    <span data-id="element-1144">
                      <strong data-id="element-1145">Relation client digitale :</strong> Animation de
                      réseaux sociaux, gestion de l'e-réputation, e-mailing.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1146">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1147" />
                    <span data-id="element-1148">
                      <strong data-id="element-1149">Fidélisation :</strong> Suivi client, gestion des
                      réclamations, ventes additionnelles.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow" data-id="element-1150">
              <CardContent className="p-8" data-id="element-1151">
                <div className="flex items-center gap-4 mb-6" data-id="element-1152">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center" data-id="element-1153">
                    <Briefcase className="w-6 h-6 text-primary" data-id="element-1154" />
                  </div>
                  <h3 className="font-extrabold font-heading text-2xl text-primary-dark" data-id="element-1155">
                    Missions TP Conseiller de Vente
                  </h3>
                </div>
                <ul className="space-y-4 text-slate-600" data-id="element-1156">
                  <li className="flex items-start gap-3" data-id="element-1157">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1158" />
                    <span data-id="element-1159">
                      <strong data-id="element-1160">Accueil et conseil :</strong> Prise en charge du
                      client, découverte des besoins, argumentation adaptée.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1161">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1162" />
                    <span data-id="element-1163">
                      <strong data-id="element-1164">Vente et encaissement :</strong> Concrétisation de
                      la vente, proposition de services annexes, gestion de la
                      caisse.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1165">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1166" />
                    <span data-id="element-1167">
                      <strong data-id="element-1168">Merchandising :</strong> Mise en rayon, maintien
                      de l'attractivité du point de vente, gestion des vitrines.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1169">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1170" />
                    <span data-id="element-1171">
                      <strong data-id="element-1172">Gestion des stocks :</strong> Réception des
                      marchandises, inventaires, suivi des ruptures.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-12 text-center" data-id="element-1173">
            <Button onClick={scrollToForm} variant="outline" className="border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white text-lg px-8 py-6 rounded-full" data-id="element-1174">
              Proposer une mission <ArrowDownCircle className="ml-2 w-5 h-5" data-id="element-1175" />
            </Button>
          </div>
        </div>
      </section>

      {/* Aides et Salaires (Vendre du rêve) */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-1176">
        <div className="container max-w-6xl mx-auto" data-id="element-1177">
          <SectionHeading title="Un recrutement très avantageux" subtitle="Profitez d'aides exceptionnelles de l'État pour l'embauche de nos alternants." centered data-id="element-1178" />

          <div className="grid md:grid-cols-3 gap-6 mt-16" data-id="element-1179">
            <Card className="bg-gradient-to-br from-primary to-primary-dark text-white border-none shadow-xl transform hover:-translate-y-2 transition-transform" data-id="element-1180">
              <CardContent className="p-8 text-center" data-id="element-1181">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1182">
                  <Euro className="w-8 h-8 text-white" data-id="element-1183" />
                </div>
                <h3 className="text-4xl font-extrabold font-heading mb-2" data-id="element-1184">
                  5 000 €*
                </h3>
                <p className="text-lg font-medium text-lavande mb-4" data-id="element-1185">par an</p>
                <p className="text-white/90" data-id="element-1186">
                  Aide pour l'embauche d'un{' '}
                  <strong data-id="element-1187">TP Conseiller de Vente</strong> (Niveau 4)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-brand-orange to-[#d6751f] text-white border-none shadow-xl transform hover:-translate-y-2 transition-transform" data-id="element-1188">
              <CardContent className="p-8 text-center" data-id="element-1189">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1190">
                  <Euro className="w-8 h-8 text-white" data-id="element-1191" />
                </div>
                <h3 className="text-4xl font-extrabold font-heading mb-2" data-id="element-1192">
                  4 500 €*
                </h3>
                <p className="text-lg font-medium text-white/80 mb-4" data-id="element-1193">par an</p>
                <p className="text-white/90" data-id="element-1194">
                  Aide pour l'embauche d'un <strong data-id="element-1195">BTS NDRC</strong> (Niveau 5)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-lavande to-[#d4c1f9] text-primary-dark border-none shadow-xl transform hover:-translate-y-2 transition-transform" data-id="element-1196">
              <CardContent className="p-8 text-center" data-id="element-1197">
                <div className="w-16 h-16 bg-primary-dark/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1198">
                  <Euro className="w-8 h-8 text-primary-dark" data-id="element-1199" />
                </div>
                <h3 className="text-4xl font-extrabold font-heading mb-2" data-id="element-1200">
                  6 000 €*
                </h3>
                <p className="text-lg font-medium text-primary-dark/70 mb-4" data-id="element-1201">
                  par an
                </p>
                <p className="text-primary-dark/90" data-id="element-1202">
                  Aide maximale pour l'embauche d'un apprenti{' '}
                  <strong data-id="element-1203">PSH</strong> (Handicap)
                </p>
              </CardContent>
            </Card>
          </div>

          <p className="text-sm text-muted-foreground mt-6 text-center max-w-3xl mx-auto" data-id="element-1204">
            * Montants applicables aux entreprises de moins de 250 salariés.
            Pour les entreprises de 250 salariés et plus, l'aide est recalculée
            selon le niveau de qualification préparé :{' '}
            <button type="button" onClick={scrollToForm} className="text-brand-orange font-semibold underline underline-offset-2 hover:text-[#E67E22] transition-colors" data-id="element-1205">
              contactez-nous pour en savoir plus
            </button>
            .
          </p>

          {/* Table des salaires */}
          <div className="mt-20" data-id="element-1206">
            <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-8 text-center" data-id="element-1207">
              Grille des salaires des alternants
            </h3>
            <div className="overflow-x-auto rounded-2xl shadow-xl border border-slate-100" data-id="element-1208">
              <table className="w-full text-left border-collapse bg-white min-w-[800px]" data-id="element-1209">
                <thead data-id="element-1210">
                  <tr className="bg-primary/5 text-primary-dark" data-id="element-1211">
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1212">
                      Âge de l'alternant
                    </th>
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1213">
                      1ère année
                    </th>
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1214">
                      2ème année
                    </th>
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1215">
                      3ème année
                    </th>
                  </tr>
                </thead>
                <tbody className="text-slate-600" data-id="element-1216">
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-1217">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1218">
                      Moins de 18 ans
                    </td>
                    <td className="p-4" data-id="element-1219">
                      27% du Smic
                      <br data-id="element-1220" />
                      <span className="font-bold text-primary text-lg" data-id="element-1221">
                        504,10 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1222">
                      39% du Smic
                      <br data-id="element-1223" />
                      <span className="font-bold text-primary text-lg" data-id="element-1224">
                        728,14 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1225">
                      55% du Smic
                      <br data-id="element-1226" />
                      <span className="font-bold text-primary text-lg" data-id="element-1227">
                        1 026,86 €
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-1228">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1229">
                      18 à 20 ans
                    </td>
                    <td className="p-4" data-id="element-1230">
                      43% du Smic
                      <br data-id="element-1231" />
                      <span className="font-bold text-primary text-lg" data-id="element-1232">
                        802,82 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1233">
                      51% du Smic
                      <br data-id="element-1234" />
                      <span className="font-bold text-primary text-lg" data-id="element-1235">
                        952,18 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1236">
                      67% du Smic
                      <br data-id="element-1237" />
                      <span className="font-bold text-primary text-lg" data-id="element-1238">
                        1 250,90 €
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-1239">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1240">
                      21 à 25 ans*
                    </td>
                    <td className="p-4" data-id="element-1241">
                      53% du Smic
                      <br data-id="element-1242" />
                      <span className="font-bold text-primary text-lg" data-id="element-1243">
                        989,52 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1244">
                      61% du Smic
                      <br data-id="element-1245" />
                      <span className="font-bold text-primary text-lg" data-id="element-1246">
                        1 138,88 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1247">
                      78% du Smic
                      <br data-id="element-1248" />
                      <span className="font-bold text-primary text-lg" data-id="element-1249">
                        1 456,28 €
                      </span>
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors" data-id="element-1250">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1251">
                      26 ans et plus*
                    </td>
                    <td className="p-4" data-id="element-1252">
                      100% du Smic
                      <br data-id="element-1253" />
                      <span className="font-bold text-primary text-lg" data-id="element-1254">
                        1 867,02 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1255">
                      100% du Smic
                      <br data-id="element-1256" />
                      <span className="font-bold text-primary text-lg" data-id="element-1257">
                        1 867,02 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1258">
                      100% du Smic
                      <br data-id="element-1259" />
                      <span className="font-bold text-primary text-lg" data-id="element-1260">
                        1 867,02 €
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-4 text-center" data-id="element-1261">
              * Ou salaire minimum conventionnel (SMC) s'il est plus favorable.
              Les montants sont donnés à titre indicatif sur la base du SMIC en
              vigueur.
            </p>
          </div>

          <div className="mt-16 text-center" data-id="element-1262">
            <Button onClick={scrollToForm} className="bg-primary hover:bg-primary-dark text-white text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-transform" data-id="element-1263">
              Estimer mon besoin de recrutement{' '}
              <ArrowDownCircle className="ml-2 w-5 h-5" data-id="element-1264" />
            </Button>
          </div>
        </div>
      </section>

      {/* Profils formés & Accompagnement */}
      <section className="py-24 bg-muted" data-id="element-1265">
        <div className="container max-w-6xl mx-auto" data-id="element-1266">
          <div className="max-w-3xl mx-auto" data-id="element-1267">
            {/* Accompagnement */}
            <div data-id="element-1268">
              <SectionHeading title="Notre accompagnement" className="mb-12" data-id="element-1269" />
              <StepList steps={ENTREPRISE_STEPS} data-id="element-1270" />

              {/* Engagement Maître d'Apprentissage */}
              <div className="mt-12 bg-lavande/20 p-8 rounded-3xl border border-lavande/50 flex gap-6 items-start group hover:-translate-y-1 transition-transform" data-id="element-1271">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-1272">
                  <Award className="w-8 h-8 text-primary-dark animate-soft-bounce" data-id="element-1273" />
                </div>
                <div data-id="element-1274">
                  <h4 className="font-extrabold font-heading text-xl text-primary-dark mb-3" data-id="element-1275">
                    L'engagement Maître d'Apprentissage
                  </h4>
                  <p className="text-primary-dark/80 leading-relaxed font-medium" data-id="element-1276">
                    En recrutant un alternant, vous vous engagez à désigner un
                    maître d'apprentissage chargé de l'accompagner, de lui
                    transmettre son savoir-faire et de suivre sa progression au
                    quotidien. Tout au long de la formation, des points
                    réguliers sont organisés entre l'entreprise, l'alternant et
                    notre équipe pédagogique. Chez Zillenium, on vous accompagne
                    pour de vrai.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Formulaire */}
      <section id="formulaire-recrutement" className="py-24 bg-primary-dark text-white relative overflow-hidden scroll-mt-16" data-id="element-1277">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1278" />
        <div className="container max-w-4xl mx-auto text-center relative z-10" data-id="element-1279">
          <h2 className="text-4xl md:text-5xl font-extrabold font-heading mb-8" data-id="element-1280">
            Confiez-nous votre recrutement
          </h2>
          <p className="text-xl md:text-2xl text-lavande mb-12 max-w-3xl mx-auto leading-relaxed font-medium" data-id="element-1281">
            "Vous cherchez à renforcer vos équipes avec un alternant motivé ?
            Remplissez ce formulaire pour nous faire part de votre besoin."
          </p>
          <div className="mt-8 text-primary-dark" data-id="element-1282">
            <EntrepriseForm data-id="element-1283" />
          </div>
          <div className="mt-12 pt-8 border-t border-white/10" data-id="element-1284">
            <Link to="/candidater" className="text-lg text-lavande hover:text-white transition-colors underline underline-offset-4" data-id="element-1285">
              Vous cherchez une formation ? Candidater
            </Link>
          </div>
        </div>
      </section>
    </div>;
}